import React from "react";
import { Listbox, ListboxButton, ListboxOption, ListboxOptions, Transition } from "@headlessui/react";

import DropdownItem, { ListItem } from "./DropdownItem";
import Spinner from "../Spinner";
import { DoubleChevronDownIcon, ChevronDownIcon } from "../../icons";

type DropdownChangeHandler<T extends string> = {
  bivarianceHack(item: ListItem<T>): void;
}["bivarianceHack"];

type DropdownProps<T extends string> = {
  label?: string;
  items: ListItem<T>[];
  selectedItem: ListItem<T> | undefined;
  placeholder?: string | React.ReactNode;
  setSelectedItem: DropdownChangeHandler<T>;
  fixedWidthItems?: boolean;
  className?: string;
  disabled?: boolean;
  isLoading?: boolean;
  loadingText?: string;
  emptyText?: string;
  inline?: boolean;
  color?: string;
  subtext?: string;
};

const DEFAULT_LOADING_TEXT = "Loading options...";
const Dropdown = <T extends string>({
  label,
  items,
  placeholder,
  selectedItem,
  setSelectedItem = () => undefined,
  fixedWidthItems: fixedWithItems = true,
  className = "",
  disabled = false,
  isLoading = false,
  loadingText = DEFAULT_LOADING_TEXT,
  emptyText,
  inline = false,
  color = undefined,
  subtext,
}: DropdownProps<T>) => {
  const SelectedLogo = selectedItem?.icon;
  const labelId = React.useId();
  const shouldShowEmptyState = items.length === 0 && emptyText !== undefined;
  const statusPlaceholder = isLoading ? (
    <span className="flex items-center gap-2">
      <Spinner.Inline className="h-4 w-4" />
      <span>{loadingText}</span>
    </span>
  ) : (
    emptyText
  );

  return (
    <div className={`${className} ${inline ? "flex items-center gap-1" : ""}`}>
      {label && (
        <label
          id={labelId}
          className={
            inline ? "text-sm text-gray-500 whitespace-nowrap" : "block text-sm font-medium text-gray-700 mb-1"
          }
        >
          {label}
        </label>
      )}
      {isLoading || shouldShowEmptyState ? (
        <div
          className={`relative w-full rounded-md bg-gray-50 px-3 py-2 text-left text-sm text-gray-500 ${
            inline ? "py-1.5" : "border border-gray-200 shadow-sm"
          }`}
        >
          {statusPlaceholder}
        </div>
      ) : items.length === 0 && placeholder ? (
        placeholder
      ) : (
        <div className="relative">
          <Listbox value={selectedItem} onChange={setSelectedItem} disabled={disabled}>
            {({ open }) => (
              <>
                <ListboxButton
                  aria-labelledby={label ? labelId : undefined}
                  data-state={open ? "open" : "closed"}
                  className={`relative w-full rounded-md pl-3 pr-8 py-2 text-left sm:text-sm focus:outline-none disabled:bg-gray-50 disabled:text-gray-500 ${
                    inline
                      ? "py-1.5"
                      : "border border-gray-300 shadow-sm focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                  }
                  ${color ? color : "bg-white"}
                  ${disabled ? "pointer-events-none" : "cursor-pointer"}
                `}
                >
                  <span className="flex items-center">
                    {SelectedLogo && <SelectedLogo className="flex-shrink-0 h-6 w-6 rounded-full mr-3" />}
                    <span className="block truncate">
                      {selectedItem?.text ? (
                        selectedItem.text
                      ) : (
                        <span className="text-gray-400 text-sm italic">Select an option</span>
                      )}
                    </span>
                  </span>
                  <span className="ml-3 absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                    {inline ? (
                      <ChevronDownIcon className="h-3.5 w-3.5 text-gray-400" />
                    ) : (
                      <DoubleChevronDownIcon className="h-5 w-5 text-gray-400" />
                    )}
                  </span>
                </ListboxButton>

                <Transition
                  show={open}
                  enter=""
                  enterFrom=""
                  enterTo=""
                  leave="transition ease-in duration-100"
                  leaveFrom="opacity-100"
                  leaveTo="opacity-0"
                >
                  <div
                    className={`z-10 absolute mt-1 ${
                      fixedWithItems ? "w-auto min-w-full" : ""
                    } rounded-md bg-white shadow-lg`}
                  >
                    <ListboxOptions
                      as="ul"
                      static
                      className="max-h-56 rounded-md py-1 text-base ring-1 ring-black/5 overflow-auto focus:outline-none sm:text-sm"
                      modal={false}
                    >
                      {items.map((item) => (
                        <ListboxOption
                          as="li"
                          key={item.key}
                          value={item}
                          disabled={item.unavailable}
                          className={({ selected }) =>
                            `${
                              selected ? "bg-gray-50" : ""
                            } text-gray-900 cursor-pointer select-none relative py-2 pl-3 pr-6 hover:bg-gray-50`
                          }
                        >
                          {({ selected }) => <DropdownItem isSelected={selected} item={item} />}
                        </ListboxOption>
                      ))}
                    </ListboxOptions>
                  </div>
                </Transition>
              </>
            )}
          </Listbox>
        </div>
      )}
      {subtext && (
        <p className="mt-2 text-xs text-gray-400 whitespace-pre-line" id="description">
          {subtext}
        </p>
      )}
    </div>
  );
};

export type DropdownListItem<T extends string> = ListItem<T>;
export default Dropdown;
